import React, { Component } from 'react'
import { RouteComponentProps } from 'react-router-dom'

import get from 'lodash/get'
import debounce from 'lodash/debounce'
import defaultTo from 'lodash/defaultTo'
import isNil from 'lodash/isNil'
import set from 'lodash/set'

import { ChildProps } from 'react-apollo'
import { InputProps } from './priceOverviewQuery'

import PageLayout from 'components/layout/pageLayout'
import SubNavigation from 'components/layout/subNavigation'
import Head from 'components/layout/head'
import Button from 'components/ui/button'
import Input from 'components/ui/input'
import { Search } from 'images/icons'
import RapidEditableTable, { IEditableItem } from 'components/layout/rapidEditableTable'
import priceOverviewTable from './priceOverviewTable.config'

interface SettingsPriceOverviewProps extends ChildProps<InputProps, Response>, RouteComponentProps {
  priceOverviewData: any
  globalProductPriceOrTimeMutation: any
  resetGlobalProductPriceMutation: any
}

interface IErrorObject {
  [k: string]: string | null
}

type SettingsPriceOverviewState = {
  errors: IErrorObject | null
  success: boolean
  hasMoreItems: boolean
  isLoading: boolean
  query: string
  page: number
  items: IEditableItem[] | null
  changedItems: IEditableItem[]
}

class SettingsPriceOverview extends Component<
  SettingsPriceOverviewProps,
  SettingsPriceOverviewState
> {
  constructor(props: any) {
    super(props)
    this.state = {
      errors: null,
      success: false,
      hasMoreItems: true,
      isLoading: false,
      query: '',
      page: 1,
      items: null,
      changedItems: [],
    }
  }

  componentWillReceiveProps = (nextProps: SettingsPriceOverviewProps) => {
    const nextPropsItems = get(nextProps, 'priceOverviewData.getPriceOverview.articles', [])

    if (nextPropsItems !== this.state.items) {
      const page = get(nextProps, 'priceOverviewData.variables.page', -1)

      if (page === 1) {
        this.setState({
          items: defaultTo(!isNil(nextPropsItems) && this.transformData(nextPropsItems), []),
        })
      }
    }
  }

  getItems = () => {
    const items = get(this.props, 'priceOverviewData.getPriceOverview.articles', [])
    const stateItems = get(this.state, 'items')

    if (!isNil(stateItems)) {
      return stateItems
    }

    return defaultTo(!isNil(items) && this.transformData(items), [])
  }

  handleSave = () => {
    const { changedItems } = this.state
    const { globalProductPriceOrTimeMutation: mutate } = this.props

    for (const item of changedItems) {
      const articleNumber = get(item, 'id')
      const value = get(item, 'data.customPrice.value')

      if (!isNil(mutate) && !isNil(articleNumber) && !isNil(value)) {
        mutate({
          variables: { articleNumber, value },
        })
          .then((res: any) => {
            // Update was successfull
            if (get(res, 'data.setGlobalCustomProductPriceOrTime')) {
              this.setState({ errors: null, success: true })

              // Toggle success message after 3 seconds
              setTimeout(() => {
                this.setState({ success: false })
              }, 3000)
            } else {
              this.setState({
                errors: {
                  api: 'Error Message',
                },
                success: false,
              })
            }
          })
          .catch(() => {})
      }
    }
  }

  handleReset = () => {
    const { resetGlobalProductPriceMutation: mutate } = this.props

    if (!isNil(mutate)) {
      this.setState({
        items: [],
        isLoading: true,
      })

      mutate()
        .then((res: any) => {
          // Reset was successfull
          if (get(res, 'data.resetAllGlobalCustomProductPrice')) {
            this.setState({ errors: null, success: true })

            // Toggle success message after 3 seconds
            setTimeout(() => {
              this.setState({ success: false })
            }, 3000)

            // Refetch the updated list
            this.fetchPage(1)
              .then((newItems: IEditableItem[]) => {
                if (get(newItems, 'length') > 0) {
                  this.setState({
                    page: 1,
                    items: newItems,
                    isLoading: false,
                    hasMoreItems: true,
                  })
                } else {
                  this.setState({
                    isLoading: false,
                    hasMoreItems: false,
                  })
                }
              })
              .catch(() => {
                this.setState({
                  isLoading: false,
                  hasMoreItems: false,
                })
              })
          } else {
            this.setState({
              isLoading: false,
              errors: {
                api: 'Error Message',
              },
              success: false,
            })
          }
        })
        .catch(() => {})
    }
  }

  handleChange = (item: IEditableItem) => {
    const { items, changedItems } = this.state

    // Update the item in the items array
    if (!isNil(items)) {
      const index = items.findIndex((i) => i.id === item.id)

      this.setState({
        items: [
          ...items.slice(0, index),
          Object.assign({}, items[index], item),
          ...items.slice(index + 1),
        ],
      })
    }

    const index = changedItems.findIndex((i) => i.id === item.id)

    // Did not find item, add it to the array
    if (index === -1) {
      this.setState({
        changedItems: [...changedItems, item],
      })
    } else {
      // Did find item, update existing item
      this.setState({
        changedItems: [
          ...changedItems.slice(0, index),
          Object.assign({}, changedItems[index], item),
          ...changedItems.slice(index + 1),
        ],
      })
    }
  }

  transformData = (articles: any) => {
    return articles.map((article: any) => {
      const { id, ...data } = article
      const customPrice = get(article, 'customPrice')

      Object.keys(data).map((key) => {
        data[key] = {
          value: data[key],
        }
        return data[key]
      })

      if (!isNil(customPrice)) {
        set(data, 'custom', true)
        set(data, 'customPrice.value', customPrice)
        set(data, 'customPrice.editable', true)
        set(data, 'customPrice.hideUnit', true)
      } else {
        set(data, 'customPrice.value', '*')
      }

      return {
        id,
        data,
      }
    })
  }

  handleQueryChange = debounce((value: string) => {
    const { isLoading } = this.state

    if (!isLoading) {
      this.setState(
        {
          query: value,
          isLoading: true,
          page: 1,
          items: [],
        },
        () => this.fetchPage(1),
      )
    }
  }, 300)

  fetchPage = (page: number) => {
    return this.props.priceOverviewData
      .refetch({
        numberOfItems: 15,
        page: page,
        filter: this.state.query,
      })
      .then((res: any) => {
        if (!isNil(get(res, 'data'))) {
          const currentPage = get(res, 'data.getPriceOverview.currentPage')
          const totalPages = get(res, 'data.getPriceOverview.totalPages')
          const articles = get(res, 'data.getPriceOverview.articles')
          const items = page === 1 ? [] : this.getItems()
          const newItems = this.transformData(defaultTo(articles, []))

          // It is the last page
          if (currentPage === totalPages) {
            this.setState({
              isLoading: false,
              hasMoreItems: false,
              items: [...items, ...newItems],
            })
          } else {
            this.setState({
              items: [...items, ...newItems],
              isLoading: false,
              hasMoreItems: true,
            })
          }
        } else {
          this.setState({
            items: [],
            isLoading: false,
            hasMoreItems: false,
          })
        }
      })
      .catch((err: any) => {
        this.setState({
          items: [],
          isLoading: false,
          hasMoreItems: false,
        })

        console.error(err)
      })
  }

  handleLoadMore = () => {
    const { isLoading, hasMoreItems, page } = this.state

    if (!isLoading && hasMoreItems) {
      const newPage = page + 1

      this.setState(
        {
          page: newPage,
          isLoading: true,
        },
        () => this.fetchPage(newPage),
      )
    }
  }

  render() {
    const { errors, success, hasMoreItems, isLoading } = this.state
    const items = this.getItems()

    return (
      <PageLayout pageTitle="Einstellungen">
        <Head
          headline="Einstellungen"
          subheadline={
            process.env.REACT_APP_ENV === 'rigips'
              ? 'Mengenermittlung und Systemsuche - HORST'
              : 'ABK calcKit – der Baukasten für die schnelle Kalkulation'
          }
          error={errors ? true : false}
          message={
            errors
              ? 'Konnte nicht gespeichert werden!'
              : success
              ? 'Änderungen gespeichert!'
              : undefined
          }
          actions={
            <>
              <Button className="mr-15" secondary onClick={this.handleReset}>
                Preise zurücksetzen
              </Button>
              <Button primary onClick={this.handleSave}>
                Speichern
              </Button>
            </>
          }
        />
        <SubNavigation
          items={[
            {
              label: 'Nutzerdaten',
              link: `/settings/user-data`,
            },
            {
              link: `/settings/logo`,
              label: 'Firmenlogo',
            },
            {
              link: `/settings/company-details`,
              label: 'Firmendaten',
            },
            {
              link: `/settings/reset-password`,
              label: 'Passwort ändern',
            },
            {
              link: `/settings/price-overview`,
              label: 'Preisübersicht',
            },
            {
              link: `/settings/time-overview`,
              label: 'Zeitenübersicht',
            },
          ]}
        />
        <div className="flex flex-col w-full px-48">
          <Input
            icon={<Search size={22} />}
            name="query"
            placeholder="Suche …"
            leftAlign
            allowClear
            onChange={(value) => this.handleQueryChange(value ? String(value) : '')}
          />
          <p className="mt-25 font-400 text-black tracking-0.1 leading-22 text-15">
            * Dieses Material kann erst verändert werden, wenn es in einem Kalkulationsblatt
            verwendet wurde.
          </p>
        </div>
        <div className="w-full mt-65 mb-65">
          <RapidEditableTable
            config={priceOverviewTable(defaultTo(items.length, 0))}
            onChange={this.handleChange}
            onLoadMore={this.handleLoadMore}
            hasMoreItems={hasMoreItems}
            isLoading={isLoading}
            data={items}
          />
        </div>
      </PageLayout>
    )
  }
}

export default SettingsPriceOverview
